import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Group,
  ShoppingCart,
  AttachMoney,
  Star,
  Timeline,
} from '@mui/icons-material';
import { getGroupBuyAnalytics } from '../../services/groupBuyService';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Analytics = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await getGroupBuyAnalytics({ days: period });
      if (response.success) {
        setAnalytics(response.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="error">
        Failed to load analytics data. Please try again.
      </Alert>
    );
  }

  const { overview, recent_activity, weekly_activity, top_performing_pools } = analytics;

  // Real data for charts
  const poolStatusData = [
    { name: 'Active', value: overview.active_pools, color: '#4caf50' },
    { name: 'Completed', value: overview.completed_pools, color: '#2196f3' },
    { name: 'Closed', value: overview.total_pools - overview.active_pools - overview.completed_pools, color: '#ff9800' },
  ];

  // Use real weekly activity data from API, fallback to empty array if no data
  const weeklyData = weekly_activity || [];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Group Buy Analytics
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Pools"
            value={overview.total_pools}
            icon={<ShoppingCart sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Pools"
            value={overview.active_pools}
            icon={<Group sx={{ fontSize: 40 }} />}
            color="success"
            subtitle={`${overview.success_rate}% success rate`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Participants"
            value={overview.total_participants}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Success Rate"
            value={`${overview.success_rate}%`}
            icon={<Star sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity ({period} days)
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>New Pools Created</Typography>
                  <Chip label={recent_activity.new_pools} color="primary" size="small" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>New Participants</Typography>
                  <Chip label={recent_activity.new_participants} color="success" size="small" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Successful Pools</Typography>
                  <Chip label={recent_activity.successful_pools} color="info" size="small" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Total Revenue</Typography>
                  <Chip label={`₦${recent_activity.total_revenue}`} color="warning" size="small" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pool Status Distribution
              </Typography>
              <Box height={200}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={poolStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {poolStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Activity Trend
              </Typography>
              <Box height={300}>
                {weeklyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="pools"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="New Pools"
                      />
                      <Line
                        type="monotone"
                        dataKey="participants"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        name="New Participants"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography variant="body2" color="textSecondary">
                      No activity data available yet
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Pools
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {top_performing_pools.map((pool, index) => (
                  <Box
                    key={pool.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={2}
                    border={1}
                    borderColor="divider"
                    borderRadius={1}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {pool.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {pool.current_quantity}/{pool.target_quantity} items • {pool.participants} participants
                      </Typography>
                    </Box>
                    <Chip
                      label={`#${index + 1}`}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Metrics */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom mb={3}>
            Detailed Metrics
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">
                  {overview.success_rate}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Average Success Rate
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main">
                  {Math.round(overview.total_participants / Math.max(overview.total_pools, 1))}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg Participants per Pool
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">
                  {Math.round((overview.total_participants / Math.max(recent_activity.new_participants, 1)) * period)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Days to Reach Target
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main">
                  {Math.round(overview.total_pools * 0.1)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pools Need Attention
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Analytics;